import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { ResidenceSection } from '../ResidenceSection';
import {
  ApartmentsInfoContainer,
  BlockRow,
  ContactLink,
  ContactsContainer,
  IconWrapper,
  OwnerContainer,
  OwnerData,
  OwnerDataContent,
  OwnerLogo,
  OwnerName,
  ownerSectionStyles,
  OwnerType,
  ScheduleAndLocationBlock,
  ScheduleAndLocationText
} from './styled';
import Icon from '../../../Icon';
import ScheduleIcon from '../../../../public/static/images/item/schedule.svg';
import LocationIcon from '../../../../public/static/images/item/location.svg';
import { ApartmentInfo } from '../../ApartmentInfo';
import { OwnerSectionProps } from './types';
import { normalizeWebsiteUrl } from '../../../../utils/helpers';
import { PlaceholderBlock } from '../../../../styled/placeholders.styled';
import useScreenSize from '../../../../hooks/useScreenSize';
import { useClient } from '../../../../hooks/useClient';

const PhonesSection = dynamic(
  () => import('../PhonesSection').then(mod => mod.PhonesSection),
  {
    ssr: false
  }
);

const BottomPhoneButton = dynamic(
  () => import('../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false,
    loading: () => <PlaceholderBlock height="60px" margin="0" />
  }
);

export const OwnerSection: React.FC<OwnerSectionProps> = memo(
  ({
    ownerName,
    clickHandler,
    phonesShown,
    phonesMasked = [],
    contactEmail,
    website,
    schedule,
    imageUrl,
    city,
    address,
    deadline,
    priceMin,
    currency,
    apartments,
    hasBillOfSale,
    isVisible,
    setIsVisible
  }) => {
    const { t } = useTranslation();
    const { isSmallOrLargeDesktop } = useScreenSize();
    const isClient = useClient();

    const scheduleAndLocationItems = useMemo(
      () => [
        {
          id: 'schedule',
          icon: ScheduleIcon,
          iconSize: { width: 18, height: 18 },
          text: schedule,
          isShown: !!schedule
        },
        {
          id: 'location',
          icon: LocationIcon,
          iconSize: { width: 15, height: 18 },
          text: `${city}${t('pages.residence.city')}, ${address}`,
          isShown: !!(city && address),
          onClick: () => {
            document
              .getElementById('location-section')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      ],
      [schedule, city, address]
    );

    const showOnDesk = (isSmallOrLargeDesktop && isClient) || !isClient;

    return (
      <ResidenceSection styles={ownerSectionStyles}>
        <OwnerContainer data-cy="owner__container">
          <OwnerData data-cy="owner">
            <OwnerDataContent>
              {ownerName && (
                <OwnerName tag="body_1" data-cy="owner_name">
                  {ownerName}
                </OwnerName>
              )}
              <OwnerType tag="body_1" data-cy="owner_type">
                {t('residences.name')}
              </OwnerType>
            </OwnerDataContent>
            {imageUrl && (
              <OwnerLogo
                src={imageUrl}
                alt={t('residences.logo', { name: ownerName })}
                width={60}
                height={60}
              />
            )}
          </OwnerData>

          <ApartmentsInfoContainer>
            <ApartmentInfo
              deadline={deadline}
              priceMin={priceMin}
              currency={currency}
              apartments={apartments}
              hasBillOfSale={hasBillOfSale}
            />
          </ApartmentsInfoContainer>

          <ScheduleAndLocationBlock>
            {scheduleAndLocationItems.map(item => {
              if (!item.isShown) return null;

              return (
                <BlockRow
                  key={item.id}
                  data-cy="residence-schedule-location-info"
                >
                  <IconWrapper>
                    <Icon
                      indent={false}
                      width={item.iconSize.width}
                      height={item.iconSize.height}
                      IconComponent={item.icon}
                    />
                  </IconWrapper>

                  <ScheduleAndLocationText
                    tag="body_3"
                    $isLocation={!!item.onClick}
                    onClick={item.onClick}
                  >
                    {item.text}
                  </ScheduleAndLocationText>
                </BlockRow>
              );
            })}
          </ScheduleAndLocationBlock>

          <div>
            {showOnDesk ? (
              <BottomPhoneButton
                stat="residence-phones-btn"
                phones={phonesShown}
                clickHandler={clickHandler}
                {...{ setIsVisible, isVisible }}
              />
            ) : (
              <PhonesSection
                phonesMasked={phonesMasked}
                onClickHandler={clickHandler}
                phonesShown={phonesShown}
              />
            )}
          </div>

          <ContactsContainer>
            {contactEmail && !!contactEmail?.length && (
              <ContactLink href={`mailto:${contactEmail}`}>
                {contactEmail}
              </ContactLink>
            )}
            {website && website.trim()?.length > 1 && (
              <ContactLink
                href={website}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {normalizeWebsiteUrl(website)}
              </ContactLink>
            )}
          </ContactsContainer>
        </OwnerContainer>
      </ResidenceSection>
    );
  }
);

OwnerSection.displayName = `OwnerSection`;
